import { Component, DestroyRef, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { TableModule, TableRowExpandEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { ClientGroupService, clientGroupMemberState } from '../../../../shared/services/client-group.service';
import { UserService } from '../../../../shared/services/user.service';
import { ToastService, serverErrorMessage } from '../../../../shared/services/toast.service';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { HelperService } from '../../../../shared/services/helper.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { constants } from '../../../../shared/constants/constants';
import { ClientGroup, ClientGroupMember, ClientGroupMemberState } from '../../../../shared/interfaces/client-group.interface';
import { UserClientVm } from '../../../../store/user/user.interface';

/**
 * Client groups management screen (MP-5715, client portal foundation).
 *
 * One screen, expandable rows: a group row expands to its members. Group
 * actions (create, rename, deactivate) ride the MP-5712 endpoints; member
 * actions (invite, edit, resend invite, deactivate / activate) ride the
 * MP-5713 ones. Member state is derived on the client: deactivated, invited
 * (set-password email unanswered) or active.
 *
 * The route is guarded and the sidenav entry hidden on the tenant's
 * `clientPortal` capability, so this component never checks the flag itself.
 */
@Component({
  selector: 'app-client-groups',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule, DialogModule, FloatLabelModule, InputTextModule,
    IconFieldModule, InputIconModule, InputSwitchModule, ChipModule, TagModule, TooltipModule, BreadcrumbModule, PipesModule
  ],
  templateUrl: './client-groups.component.html',
  styleUrl: './client-groups.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ClientGroupsComponent implements OnInit {
  private readonly clientGroupService = inject(ClientGroupService);
  private readonly userService = inject(UserService);
  private readonly toast = inject(ToastService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  readonly helperService = inject(HelperService);

  readonly user = signal<UserClientVm | null>(null);
  /** Backend (MP-5712) authorizes FIRM_MANAGER and PROJECT_OWNER only. */
  readonly canManage = computed(() => {
    const role = this.user()?.role;
    return role === constants.userRoles.firmManager || role === constants.userRoles.projectOwner;
  });
  readonly breadcrumbItems = signal<MenuItem[]>([{ label: 'Firm' }, { label: 'Client Groups' }]);

  // Group list
  readonly groups = signal<ClientGroup[]>([]);
  readonly includeInactive = signal(false);
  readonly searchText = signal('');
  readonly filteredGroups = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    const groups = this.groups();
    return query ? groups.filter((group) => group.name.toLowerCase().includes(query)) : groups;
  });
  readonly rowsPerPage = signal(10);

  // Row expansion: one group open at a time, members fetched on expand.
  readonly expandedRows = signal<Record<string, boolean>>({});
  readonly expandedGroupId = signal<string | null>(null);
  readonly expandedGroup = computed(() => this.groups().find((group) => group._id === this.expandedGroupId()) ?? null);
  readonly members = signal<ClientGroupMember[]>([]);
  readonly membersLoading = signal(false);
  /** Guards against a slow response for a group that is no longer expanded. */
  private readonly memberRequestSeq = signal(0);

  // Invite / edit member dialog (MP-5713)
  readonly memberDialogVisible = signal(false);
  readonly editingMember = signal<ClientGroupMember | null>(null);
  readonly isMemberEdit = computed(() => this.editingMember() !== null);
  readonly memberDialogTitle = computed(() => this.isMemberEdit() ? 'Edit Client User' : 'Invite Client User');
  readonly memberSubmitLabel = computed(() => this.isMemberEdit() ? 'UPDATE' : 'INVITE');
  /** Activate / Deactivate lives in the edit dialog footer, as on Firm Users. */
  readonly memberStatusButtonLabel = computed(() => this.editingMember()?.isActive ? 'Deactivate' : 'Activate');
  readonly memberFirstName = signal('');
  readonly memberLastName = signal('');
  readonly memberEmail = signal('');
  /** Display form 000-000-0000; the backend stores digits only. */
  readonly memberPhone = signal('');
  readonly memberNameMaxLength = signal(100);
  readonly memberPhonePattern = signal(/^\d{3}-\d{3}-\d{4}$/);
  readonly isMemberEmailValid = computed(() => constants.emailRegex.test(this.memberEmail().trim()));
  readonly isMemberPhoneValid = computed(() => {
    const phone = this.memberPhone().trim();
    return phone === '' || this.memberPhonePattern().test(phone);
  });
  readonly isMemberFormValid = computed(() => {
    const nameOk = (value: string) => {
      const length = value.trim().length;
      return length > 0 && length <= this.memberNameMaxLength();
    };
    return nameOk(this.memberFirstName()) && nameOk(this.memberLastName())
      && (this.isMemberEdit() || this.isMemberEmailValid()) && this.isMemberPhoneValid();
  });
  readonly memberSaving = signal(false);

  // Create / rename dialog
  readonly dialogVisible = signal(false);
  readonly editingGroup = signal<ClientGroup | null>(null);
  readonly isEdit = computed(() => this.editingGroup() !== null);
  readonly dialogTitle = computed(() => this.isEdit() ? 'Rename Client Group' : 'Create Client Group');
  readonly submitLabel = computed(() => this.isEdit() ? 'UPDATE' : 'CREATE');
  readonly groupName = signal('');
  /** Matches the backend revalidator schema (1-100 chars). */
  readonly groupNameMaxLength = signal(100);
  readonly trimmedGroupName = computed(() => this.groupName().trim());
  readonly isGroupNameValid = computed(() => {
    const length = this.trimmedGroupName().length;
    return length > 0 && length <= this.groupNameMaxLength();
  });
  readonly saving = signal(false);

  ngOnInit(): void {
    this.user.set(this.userService.getSessionUser());
    this.loadGroups();
  }

  loadGroups(): void {
    this.clientGroupService.getGroups(this.includeInactive())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (groups) => {
          this.groups.set(this.sortByNewest(groups ?? []));
          this.collapseAll();
        },
        error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to load client groups.'))
      });
  }

  onIncludeInactiveChange(includeInactive: boolean): void {
    this.includeInactive.set(includeInactive);
    this.loadGroups();
  }

  onRemoveSearchChip(): void {
    this.searchText.set('');
  }

  onRowExpand(event: TableRowExpandEvent): void {
    const group = event.data as ClientGroup;
    this.expandedRows.set({ [group._id]: true });
    this.expandedGroupId.set(group._id);
    this.loadMembers(group._id);
  }

  loadMembers(groupId: string): void {
    const seq = this.memberRequestSeq() + 1;
    this.memberRequestSeq.set(seq);
    this.members.set([]);
    this.membersLoading.set(true);
    this.clientGroupService.getMembers(groupId)
      .pipe(finalize(() => {
        if (seq === this.memberRequestSeq()) {
          this.membersLoading.set(false);
        }
      }), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (members) => {
          if (seq !== this.memberRequestSeq() || this.expandedGroupId() !== groupId) {
            return; // the row was collapsed or another group expanded meanwhile
          }
          this.members.set(members ?? []);
        },
        error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to load client users.'))
      });
  }

  /**
   * Member pill. A deactivated group blocks every member at the token layer
   * (MP-5713), so its members render as Deactivated whatever their own flag;
   * the member table only ever shows the expanded group's users.
   */
  memberState(member: ClientGroupMember): ClientGroupMemberState {
    if (this.expandedGroup()?.isActive === false) {
      return 'deactivated';
    }
    return clientGroupMemberState(member);
  }

  memberStateLabel(member: ClientGroupMember): string {
    switch (this.memberState(member)) {
      case 'invited': return 'Invited';
      case 'deactivated': return 'Deactivated';
      default: return 'Active';
    }
  }

  onRowCollapse(): void {
    this.collapseAll();
  }

  showCreateDialog(): void {
    this.editingGroup.set(null);
    this.groupName.set('');
    this.dialogVisible.set(true);
  }

  showRenameDialog(group: ClientGroup): void {
    this.editingGroup.set(group);
    this.groupName.set(group.name);
    this.dialogVisible.set(true);
  }

  hideDialog(): void {
    this.dialogVisible.set(false);
  }

  onSubmit(): void {
    if (!this.isGroupNameValid() || this.saving()) {
      return;
    }
    const name = this.trimmedGroupName();
    const editing = this.editingGroup();
    const request$ = editing
      ? this.clientGroupService.renameGroup(editing._id, name)
      : this.clientGroupService.createGroup(name);

    this.saving.set(true);
    request$
      .pipe(finalize(() => this.saving.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.hideDialog();
          this.toast.showSuccess(editing ? 'Client group renamed successfully.' : 'Client group created successfully.');
          this.loadGroups();
        },
        error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to save client group.'))
      });
  }

  deactivateGroup($event: Event, group: ClientGroup): void {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: `You are about to deactivate ${group.name}. Client users in this group will no longer be able to sign in. Are you sure?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.clientGroupService.deactivateGroup(group._id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toast.showSuccess('Client group deactivated successfully.');
              this.loadGroups();
            },
            error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to deactivate client group.'))
          });
      }
    });
  }

  showInviteDialog(): void {
    this.editingMember.set(null);
    this.memberFirstName.set('');
    this.memberLastName.set('');
    this.memberEmail.set('');
    this.memberPhone.set('');
    this.memberDialogVisible.set(true);
  }

  showEditMemberDialog(member: ClientGroupMember): void {
    this.editingMember.set(member);
    this.memberFirstName.set(member.firstName);
    this.memberLastName.set(member.lastName);
    this.memberEmail.set(member.email);
    this.memberPhone.set(member.phone ? this.helperService.formatPhoneString(member.phone) : '');
    this.memberDialogVisible.set(true);
  }

  hideMemberDialog(): void {
    this.memberDialogVisible.set(false);
  }

  onMemberPhoneInput(value: string): void {
    this.memberPhone.set(this.helperService.formatPhone(value ?? ''));
  }

  onMemberSubmit(): void {
    const groupId = this.expandedGroupId();
    if (!groupId || !this.isMemberFormValid() || this.memberSaving()) {
      return;
    }
    const editing = this.editingMember();
    const phone = this.memberPhone().trim();
    const names = { firstName: this.memberFirstName().trim(), lastName: this.memberLastName().trim() };
    const request$ = editing
      ? this.clientGroupService.updateMember(groupId, editing._id, { ...names, phone })
      : this.clientGroupService.inviteMember(groupId, { ...names, email: this.memberEmail().trim(), phone });

    this.memberSaving.set(true);
    request$
      .pipe(finalize(() => this.memberSaving.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.hideMemberDialog();
          this.toast.showSuccess(editing ? 'Client user updated successfully.' : 'Invite email has been sent.');
          this.loadMembers(groupId);
        },
        error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to save client user.'))
      });
  }

  resendInvite($event: Event, member: ClientGroupMember): void {
    $event.stopPropagation();
    const groupId = this.expandedGroupId();
    if (!groupId) {
      return;
    }
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: `You are about to send re-invite email to ${member.firstName} ${member.lastName}. Are you sure?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.clientGroupService.resendInvite(groupId, member._id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.toast.showSuccess('Re-invite email has been sent.'),
            error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to resend the invite.'))
          });
      }
    });
  }

  /** Firm Users pattern: status button in the edit dialog footer, no extra confirmation. */
  onMemberStatusChange(): void {
    const groupId = this.expandedGroupId();
    const member = this.editingMember();
    if (!groupId || !member || this.memberSaving()) {
      return;
    }
    const activate = !member.isActive;
    const verb = activate ? 'activated' : 'deactivated';
    this.memberSaving.set(true);
    this.clientGroupService.setMemberStatus(groupId, member._id, activate)
      .pipe(finalize(() => this.memberSaving.set(false)), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.hideMemberDialog();
          this.toast.showSuccess(`Client user ${verb} successfully.`);
          this.loadMembers(groupId);
        },
        error: (err) => this.toast.showError(serverErrorMessage(err, `Unable to ${activate ? 'activate' : 'deactivate'} client user.`))
      });
  }

  /** Active users only; reuses the forgot-password call like Firm Users. */
  sendPasswordReset($event: Event, member: ClientGroupMember): void {
    $event.stopPropagation();
    this.authService.forgotPassword({ email: member.email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.toast.showSuccess('An email has been sent to the client user to reset their password.'),
        error: (err) => this.toast.showError(serverErrorMessage(err, 'Unable to send the password reset link.'))
      });
  }

  /** Most recently created first; the backend returns insertion order. */
  private sortByNewest(groups: ClientGroup[]): ClientGroup[] {
    return [...groups].sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
  }

  private collapseAll(): void {
    this.memberRequestSeq.update((seq) => seq + 1);
    this.expandedRows.set({});
    this.expandedGroupId.set(null);
    this.members.set([]);
    this.membersLoading.set(false);
  }
}
